import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { ShortUrlObjectType } from '@air/api/types';
import { Modal } from '@air/primitive-modal';
import { useToasts } from '@air/provider-toast';

import { NoPermissionToCreateShareLink } from '~/components/NoPermissionToCreateShareLink';
import { SharePrivateCanvasLinkInfo } from '~/components/SharePrivateCanvasModal/SharePrivateCanvasLinkInfo';
import { SharePrivateCanvasModalHeader } from '~/components/SharePrivateCanvasModal/SharePrivateCanvasModalHeader';
import { ReadonlySharePrivateLink } from '~/components/SharePrivateContentModal/ReadonlySharePrivateLink';
import { ShareLinkTeamInviteNudgePanel } from '~/components/SharePrivateContentModal/ShareLinkTeamInviteNudgePanel';
import { SharePrivateContentSpinner } from '~/components/SharePrivateContentModal/SharePrivateContentSpinner';
import { SHARE_PRIVATE_CONTENT_MODAL } from '~/constants/testIDs';
import { useShowTurnOnShareLinkNotifications } from '~/hooks/turnOnEmailShareNotificationsToast/useShowTurnOnShareLinkNotifications';
import { useShowShareLinkTeamInviteNudgeForModal } from '~/hooks/useShowShareLinkTeamInviteNudgeForModal';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useGetExistingShortIdInformation } from '~/swr-hooks/shortUrl/useGetExistingShortIdInformation';
import { useGetShortIdForCanvas } from '~/swr-hooks/shortUrl/useGetShortIdForCanvas';
import { canCreateShortUrl, canEditShortUrl, canViewShortUrl } from '~/utils/permissions/shortUrlPermissions';

export type SharePrivateCanvasModalProps = AirModalProps<{
  canvasId: string;
  trackLocation: AirActionTrackingLocation;
}>;

export const SharePrivateCanvasModal = memo(({ canvasId, onClose, trackLocation }: SharePrivateCanvasModalProps) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();

  const { showToast, setToastXOffset, getToastXOffset, setToastRightOffset, getToastRightOffset } = useToasts();
  const [hasCreatedShareLinkInThisSession, setHasCreatedShareLinkInThisSession] = useState(false);
  const { showTurnOnShareLinkNotifications } = useShowTurnOnShareLinkNotifications();

  const previousXOffset = useRef(getToastXOffset());
  const previousRightOffset = useRef(getToastRightOffset());

  useEffect(() => {
    const prevX = previousXOffset.current;
    const prevRight = previousRightOffset.current;
    setToastXOffset(0);
    setToastRightOffset(0);
    return () => {
      setToastXOffset(prevX);
      setToastRightOffset(prevRight);
    };
  }, [setToastXOffset, setToastRightOffset]);

  const canEdit = canEditShortUrl(workspacePermissions);
  const canCreate = canCreateShortUrl(workspacePermissions);

  const { data: shortIdInfo, isLoading: isInitialLoading } = useGetExistingShortIdInformation({
    id: canvasId,
    type: ShortUrlObjectType.canvas,
    boardContext: undefined,
    canView: canViewShortUrl(workspacePermissions),
  });

  const {
    mutate: createShortIdForCanvas,
    isPending: isCreatingShortIdForCanvas,
    isError: isErrorCreatingShortIdForCanvas,
  } = useGetShortIdForCanvas();

  const hasShortId = !!shortIdInfo;
  const handleShareLinkCreatedFallback = useCallback(() => {
    showTurnOnShareLinkNotifications({
      onManageClick: onClose,
    });
  }, [onClose, showTurnOnShareLinkNotifications]);
  const { isShareLinkTeamInviteNudgeVisible, openShareLinkTeamInviteNudgeModal, workspaceName } =
    useShowShareLinkTeamInviteNudgeForModal({
      isShareLinkCreated: hasCreatedShareLinkInThisSession,
      isShareLinkReady: hasShortId,
      location: 'canvas_share_link',
      onShareLinkCreatedFallback: handleShareLinkCreatedFallback,
    });

  useEffect(() => {
    const workspaceId = currentWorkspace?.id;

    if (!workspaceId) {
      return;
    }
    if (
      !isInitialLoading &&
      !hasShortId &&
      canCreate &&
      !isCreatingShortIdForCanvas &&
      !isErrorCreatingShortIdForCanvas
    ) {
      createShortIdForCanvas(
        { workspaceId, id: canvasId },
        {
          onSuccess: () => {
            setHasCreatedShareLinkInThisSession(true);
          },
          onError: () => {
            showToast('Something went wrong. Please try again later.', {
              color: 'red',
            });
          },
        },
      );
    }
  }, [
    currentWorkspace?.id,
    canvasId,
    canCreate,
    createShortIdForCanvas,
    isInitialLoading,
    isCreatingShortIdForCanvas,
    isErrorCreatingShortIdForCanvas,
    showToast,
    hasShortId,
  ]);

  const modalContent = useMemo(() => {
    if (!workspacePermissions) {
      return <SharePrivateContentSpinner />;
    } else if (!shortIdInfo) {
      if (canCreate) {
        return <SharePrivateContentSpinner />;
      } else {
        return <NoPermissionToCreateShareLink />;
      }
    } else {
      if (!canEdit) {
        return (
          <ReadonlySharePrivateLink
            objectType="canvas"
            shareId={shortIdInfo.id}
            objectId={canvasId}
            trackLocation={trackLocation}
          />
        );
      } else {
        return (
          <SharePrivateCanvasLinkInfo canvasId={canvasId} trackLocation={trackLocation} shortIdInfo={shortIdInfo} />
        );
      }
    }
  }, [canvasId, canCreate, canEdit, workspacePermissions, shortIdInfo, trackLocation]);

  if (showingSubscriptionExpiredModal) {
    return null;
  }

  return (
    <Modal
      data-testid={SHARE_PRIVATE_CONTENT_MODAL}
      dangerouslyBypassFocusLock // allows nested modals (e.g. regenerate confirmation) to open without focus lock conflict
      isOpen
      onDismiss={onClose}
      className="bg-transparent p-0 focus-visible:ring-0"
    >
      <div className="rounded-6 bg-surface-1">
        <SharePrivateCanvasModalHeader onClose={onClose} />
        {modalContent}
      </div>
      {isShareLinkTeamInviteNudgeVisible && (
        <ShareLinkTeamInviteNudgePanel
          onInviteClick={openShareLinkTeamInviteNudgeModal}
          workspaceName={workspaceName}
        />
      )}
    </Modal>
  );
});

SharePrivateCanvasModal.displayName = 'SharePrivateCanvasModal';
